// ─────────────────────────────────────────────────────────────
//  SalesGod Value-Based Pricing Engine
//
//  Diese Datei ist das Frontend-Spiegelbild von VALUE_PRICING in
//  functions/generateValueBasedOffer.js (server-seitig, autoritativ)
//  und entspricht dem Live-Preview-Pendant aus Configurator.js.
//
//  WICHTIG ZUR WARTUNG: Wenn sich Konstanten oder Formeln im Backend
//  ändern, müssen sie hier synchron angepasst werden – sonst weicht die
//  Live-Preview vom tatsächlich generierten Angebot ab.
// ─────────────────────────────────────────────────────────────

// 1. Basispreise & Floor (monatlich, EUR) – Foundation / Plus / Premium
export const BASE_PRICES = [1640, 2860, 5460];
export const MIN_PRICES = [1640, 2860, 5460]; // absolute Untergrenze – nie darunter
export const TIER_NAMES = ['Foundation', 'Plus', 'Premium'];

// Erwartete Termine/Monat pro Tier (für Projektionen)
export const TIER_EXPECTED_APPOINTMENTS = [6, 12, 24];

// Appointment-Value Ökonomie
//   Der Termin-Preis nutzt eine FIXE Referenz-Abschlussquote (APPT_REFERENCE_CLOSE_RATE)
//   und ist damit bewusst vom "Abschlüsse pro Termin"-Slider entkoppelt: dieser Slider
//   steuert ausschließlich die ROI-/Neukunden-Szenarien, NICHT unseren Preis.
export const APPT_REFERENCE_CLOSE_RATE = 0.30; // FIXE Conversion für den Termin-Preis
export const APPT_CAPTURE_RATE = 0.16; // wir berechnen 16 % des Kundenprofits/Termin
export const APPT_REFERENCE_MARGIN = 0.30; // FIXE Referenzmarge für den Termin-Bonus.
//   Bewusst entkoppelt von der Live-Marge: wenn der Kunde seine echte Marge hochdreht,
//   soll SEIN ROI steigen – nicht unsere Fee. Sonst entsteht ein Nullsummenspiel.
export const FREE_APPTS_PER_MONTH = 3; // erste 3 Termine frei

// Szenario-Spread: konservative/starke Quote relativ zur live einstellbaren "Abschlüsse
// pro Termin"-Quote (closeRate). Faktoren 0,4 (konservativ) / 1,0 (realistisch) / 1,7 (stark).
export const SCENARIO_SPREAD = { conservative: 0.4, strong: 1.7 };

// Setup-Pakete (separat, nicht multipliziert)
export const SETUP_STANDARD = 0;
export const SETUP_PREMIUM = 3240;

// ── Helper ──
export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
// Preis-Rundung: immer auf die nächste Zahl, die auf ...60 endet (z. B. 2.960 / 3.060 / 6.660).
const roundToSixty = (v) => Math.round((v - 60) / 100) * 100 + 60;
const roundToTen = (v) => Math.round(v / 10) * 10;

// 2. Price Multiplier (Deal-Wert → Faktor), stückweise linear – BEWUSST GEDÄMPFT.
//    Der Preis soll nur minimal mit dem Deal-Wert mitgehen (max ~1,35x bei 100k),
//    damit der Kunde nicht von der Fee erdrückt wird und der Mehrwert bei ihm bleibt.
export function priceMultiplier(revenuePerClose) {
  const d = clamp(revenuePerClose, 100, 100000);
  let m;
  if (d <= 10000) m = 0.95 + ((d - 1000) / 9000) * 0.05; // 0,95 → 1,00
  else if (d <= 30000) m = 1.0 + ((d - 10000) / 20000) * 0.08; // 1,00 → 1,08
  else if (d <= 50000) m = 1.08 + ((d - 30000) / 20000) * 0.07; // 1,08 → 1,15
  else if (d <= 75000) m = 1.15 + ((d - 50000) / 25000) * 0.1; // 1,15 → 1,25
  else m = 1.25 + ((d - 75000) / 25000) * 0.1; // 1,25 → 1,35
  return m;
}

// Quality-Nudge (±3 %) aus drei Scores (0–10)
export function qualityFactor({ uniquenessScore = 5, marketNeedScore = 5, blueOceanScore = 5 } = {}) {
  const q = (uniquenessScore + marketNeedScore + blueOceanScore) / 3 / 10;
  if (q < 0.4) return 0.97;
  if (q > 0.7) return 1.03;
  return 1.0;
}

// Effektiver Multiplikator (nur wenn valueBasedPricingEnabled)
export function effectiveMultiplier({ revenuePerClose, quality, valueBasedPricingEnabled }) {
  if (!valueBasedPricingEnabled) return 1;
  return priceMultiplier(revenuePerClose) * qualityFactor(quality);
}

// 3. Finale Preisberechnung pro Tier
//    Hochskalierte Preise werden auf die nächste ...60-Zahl gerundet.
//    Der Basispreis/Floor bleibt unangetastet (kann auf ...40 enden, z. B. Foundation 1.640).
export function tierPrice(tierIndex, multiplier) {
  const base = BASE_PRICES[tierIndex];
  const min = MIN_PRICES[tierIndex];
  const raw = base * multiplier;
  if (raw <= min) return min; // exakt auf Floor snappen
  return Math.max(min, roundToSixty(raw));
}

// 4. Appointment Value (Bonus pro Termin ab dem 4.)
//    Wertbasiert: pro Termin entsteht beim Kunden ein erwarteter Profit
//    (Deal-Wert × fixe Referenzmarge × FIXE Referenz-Abschlussquote). Davon berechnen wir 16 %.
//    Reagiert live auf den Deal-Wert, ist aber bewusst von der Live-Marge UND von der
//    eingestellten "Abschlüsse pro Termin"-Quote entkoppelt – der Slider treibt nur den ROI.
export function appointmentValue(revenuePerClose) {
  const d = clamp(revenuePerClose, 100, 100000);
  const customerProfitPerAppt = d * APPT_REFERENCE_MARGIN * APPT_REFERENCE_CLOSE_RATE;
  const raw = customerProfitPerAppt * APPT_CAPTURE_RATE;
  return roundToTen(Math.max(15, raw));
}

// 5. Agentur-/ROI-Projektion (nur intern relevant, hier zur Berechnung)
export function computeProjection(config) {
  const {
    revenuePerClose,
    profitMargin,
    tier,
    valueBasedPricingEnabled,
    quality,
    expectedApptsOverride,
    closeRate = APPT_REFERENCE_CLOSE_RATE,
    apptValueMode = 'auto',
    apptValueManual = 0,
    monthlyPriceMode = 'auto',
    monthlyPriceManual = 0,
    setup = SETUP_STANDARD,
  } = config;

  const mult = effectiveMultiplier({ revenuePerClose, quality, valueBasedPricingEnabled });
  const autoMonthlyPrice = tierPrice(tier, mult);
  // Manueller Override für den monatlichen Retainer: Wert kommt aus dem Slider, fließt
  // aber identisch in alle Folgeberechnungen (ourMonthly, ROI, Szenarien) ein.
  const monthlyPrice = monthlyPriceMode === 'manual' ? clamp(Math.round(monthlyPriceManual), 0, 50000) : autoMonthlyPrice;
  const autoApptValue = appointmentValue(revenuePerClose);
  // Manueller Override: Wert kommt aus dem Slider, fließt aber identisch in die Berechnung.
  const apptValue = apptValueMode === 'manual' ? clamp(Math.round(apptValueManual), 0, 5000) : autoApptValue;
  const expectedAppts = expectedApptsOverride ?? TIER_EXPECTED_APPOINTMENTS[tier];
  const billableApptsPerMonth = Math.max(0, expectedAppts - FREE_APPTS_PER_MONTH);

  const ourMonthly = monthlyPrice + billableApptsPerMonth * apptValue;
  const ourAnnual = 12 * ourMonthly;
  const investmentYear1 = ourAnnual + setup;

  const profitPerClose = revenuePerClose * profitMargin;

  return {
    multiplier: mult,
    monthlyPrice,
    autoMonthlyPrice,
    monthlyPriceMode,
    appointmentValue: apptValue,
    autoApptValue,
    apptValueMode,
    closeRate,
    expectedAppts,
    billableApptsPerMonth,
    ourMonthly,
    ourAnnual,
    setup,
    investmentYear1,
    profitPerClose,
    tierName: TIER_NAMES[tier],
  };
}

// ── Formatierung (de-DE) ──
const nfEUR = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
export const formatEUR = (n) => `${nfEUR.format(Math.round(n))} €`;
export const formatPct = (frac) => `${Math.round(frac * 100)} %`;
